import { request, readExtras, writeExtras } from "./plannerStorage";
import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

// --- Icons ---
const IconPlus = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);
const IconX = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
);
const IconTrash = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
);

const DEFAULT_CATEGORIES = [
  { id: "1", label: "อาหาร", emoji: "🍔", color: "#F6AD55" },
  { id: "2", label: "เดินทาง", emoji: "🚕", color: "#F6E05E" },
  { id: "3", label: "ช้อปปิ้ง", emoji: "🛍️", color: "#63B3ED" },
  { id: "4", label: "ที่พัก", emoji: "🏨", color: "#FC8181" },
  { id: "5", label: "กิจกรรม", emoji: "🎟️", color: "#F687B3" },
  { id: "6", label: "อื่นๆ", emoji: "📝", color: "#94A3B8" },
];

const EXTENDED_COLORS = [
  "#4FD1C5", "#9F7AEA", "#F6E05E", "#FC8181", "#63B3ED",
  "#F6AD55", "#F687B3", "#34D399", "#A78BFA", "#F87171",
];

const AccountModal = ({ form, setForm, handleSubmit, setShowModal }) => {
  return (
    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="bg-slate-900 w-full max-w-md p-6 rounded-3xl border border-slate-700 shadow-2xl animate-fadeIn max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">
            {form.id ? "แก้ไขบัญชี" : "เพิ่มบัญชีรับเงิน"}
          </h3>
          <button type="button" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
            <IconX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs font-bold text-slate-400 ml-1 block mb-2">ชื่อบัญชี / ชื่อคนรับ (เช่น เจ้าของหอ, บัญชีออมทรัพย์)</label>
            <input
              type="text"
              className="w-full bg-slate-950 p-3.5 rounded-xl border border-slate-800 text-white outline-none focus:border-indigo-500 transition"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 ml-1 block mb-2">เลขบัญชี (ถ้ามี)</label>
            <input
              type="text"
              className="w-full bg-slate-950 p-3.5 rounded-xl border border-slate-800 text-white outline-none focus:border-indigo-500 transition font-mono"
              placeholder="123-4-56789-0"
              value={form.accountNumber}
              onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 ml-1 block mb-2">แนบรูป QR Code พร้อมเพย์ (ถ้ามี)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setForm({ ...form, qrImage: reader.result });
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="w-full bg-slate-950 p-2.5 rounded-xl border border-slate-800 text-slate-400 text-sm outline-none transition file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-indigo-500/10 file:text-indigo-400 hover:file:bg-indigo-500/20"
            />
            {form.qrImage && (
              <div className="mt-3 relative inline-block">
                <img src={form.qrImage} alt="QR Code" className="h-28 object-contain rounded-xl border border-slate-700 bg-white p-1" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, qrImage: "" })}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3.5 rounded-xl shadow-lg mt-4 transition">
            บันทึกบัญชี
          </button>
        </form>
      </div>
    </div>
  );
};

const PlanModal = ({ form, setForm, handleSubmit, setShowModal, categories, setCategories, expenses, setExpenses }) => {
  const [isAddingCat, setIsAddingCat] = useState(false);
  const [newEmoji, setNewEmoji] = useState("");
  const [newLabel, setNewLabel] = useState("");

  const [editingCatId, setEditingCatId] = useState(null);
  const [editEmoji, setEditEmoji] = useState("");
  const [editLabel, setEditLabel] = useState("");

  const saveNewCategory = () => {
    if (!newLabel.trim()) return;
    const nextColor = EXTENDED_COLORS[categories.length % EXTENDED_COLORS.length];
    const newCat = {
      id: Date.now().toString(),
      label: newLabel.trim(),
      emoji: newEmoji.trim() || "📌",
      color: nextColor,
    };
    setCategories([...categories, newCat]);
    setForm({ ...form, category: newCat.label });
    setIsAddingCat(false);
    setNewEmoji("");
    setNewLabel("");
  };

  const startEditCategory = (cat, e) => {
    e.stopPropagation();
    setEditingCatId(cat.id);
    setEditEmoji(cat.emoji);
    setEditLabel(cat.label);
    setIsAddingCat(false);
  };

  const saveEditCategory = (id) => {
    if (!editLabel.trim()) return;
    const oldCat = categories.find((c) => c.id === id);
    const newLabelText = editLabel.trim();
    const updatedCategories = categories.map((c) =>
      c.id === id ? { ...c, label: newLabelText, emoji: editEmoji.trim() || "📌" } : c
    );
    setCategories(updatedCategories);

    if (form.category === oldCat.label) {
      setForm({ ...form, category: newLabelText });
    }
    const updatedExpenses = expenses.map((exp) =>
      exp.category === oldCat.label ? { ...exp, category: newLabelText } : exp
    );
    setExpenses(updatedExpenses);
    setEditingCatId(null);
  };

  const handleDeleteCategory = (id, e) => {
    e.stopPropagation();
    if (window.confirm("ต้องการลบหมวดหมู่นี้ใช่ไหม?")) {
      setCategories(categories.filter((c) => c.id !== id));
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="bg-slate-900 w-full max-w-md p-6 rounded-3xl border border-slate-700 shadow-2xl animate-fadeIn max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">
            {form.id ? "แก้ไขรายการ" : "เพิ่มรายการที่ต้องจ่าย"}
          </h3>
          <button type="button" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
            <IconX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs font-bold text-slate-400 ml-1 block mb-2">ชื่อรายการ (เช่น ค่าเช่าห้อง, เน็ตบ้าน)</label>
            <input
              type="text"
              className="w-full bg-slate-950 p-3.5 rounded-xl border border-slate-800 text-white outline-none focus:border-emerald-500 transition"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 ml-1 block mb-2">ยอดเงินที่คาดว่าจะจ่าย (บาท)</label>
            <input
              type="number"
              className="w-full bg-slate-950 p-3.5 rounded-xl border border-slate-800 text-white outline-none focus:border-emerald-500 font-bold text-red-400 transition"
              placeholder="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 ml-1 mb-3 block">หมวดหมู่</label>
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1 scrollbar-thin">
              {categories.map((cat) => {
                const isActive = form.category === cat.label;
                if (editingCatId === cat.id) {
                  return (
                    <div key={cat.id} className="flex items-center gap-2 px-3 py-1 rounded-full border border-yellow-500/50 bg-yellow-500/10">
                      <input type="text" className="w-6 bg-transparent text-center outline-none text-sm" value={editEmoji} onChange={(e) => setEditEmoji(e.target.value)} maxLength={2} />
                      <div className="w-px h-4 bg-yellow-500/30"></div>
                      <input type="text" className="w-20 bg-transparent outline-none text-xs font-bold text-white" value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
                      <button type="button" onClick={() => saveEditCategory(cat.id)} className="text-yellow-400 hover:text-yellow-300 font-bold ml-1">✓</button>
                      <button type="button" onClick={() => setEditingCatId(null)} className="text-slate-400 hover:text-red-400 font-bold ml-1">✕</button>
                    </div>
                  );
                }
                return (
                  <div key={cat.id} className="relative group">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, category: cat.label })}
                      className="flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-bold transition-all"
                      style={isActive ? { borderColor: cat.color, color: cat.color, backgroundColor: `${cat.color}1A` } : { borderColor: "#1e293b", color: "#94a3b8", backgroundColor: "#020617" }}
                    >
                      <span className="text-sm">{cat.emoji}</span>
                      <span>{cat.label}</span>
                    </button>
                    <div className="absolute -top-2 -right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-all shadow-lg rounded-full bg-slate-800 p-0.5 border border-slate-600">
                      <button type="button" onClick={(e) => startEditCategory(cat, e)} className="text-slate-300 hover:text-white hover:bg-blue-500 rounded-full w-5 h-5 flex items-center justify-center transition-colors">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                      </button>
                      <button type="button" onClick={(e) => handleDeleteCategory(cat.id, e)} className="text-slate-300 hover:text-white hover:bg-red-500 rounded-full w-5 h-5 flex items-center justify-center text-[10px] transition-colors font-bold">✕</button>
                    </div>
                  </div>
                );
              })}
              {!isAddingCat ? (
                <button type="button" onClick={() => setIsAddingCat(true)} className="flex items-center gap-1 px-4 py-2 rounded-full border border-dashed border-slate-700 bg-slate-900 text-slate-400 text-xs font-bold hover:text-white hover:border-slate-500 transition-all">
                  <IconPlus size={14} /> เพิ่ม
                </button>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/50 bg-emerald-500/10">
                  <input type="text" placeholder="😀" className="w-6 bg-transparent text-center outline-none text-sm" value={newEmoji} onChange={(e) => setNewEmoji(e.target.value)} maxLength={2} />
                  <div className="w-px h-4 bg-emerald-500/30"></div>
                  <input type="text" placeholder="ชื่อหมวด" className="w-20 bg-transparent outline-none text-xs font-bold text-white placeholder:font-normal placeholder:text-emerald-500/50" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
                  <button type="button" onClick={saveNewCategory} className="text-emerald-400 hover:text-emerald-300 font-bold ml-1">✓</button>
                  <button type="button" onClick={() => setIsAddingCat(false)} className="text-slate-400 hover:text-red-400 font-bold ml-1">✕</button>
                </div>
              )}
            </div>
          </div>

          <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 rounded-xl shadow-lg mt-4 transition">
            เสร็จสิ้น
          </button>
        </form>
      </div>
    </div>
  );
};

export default function PlannerPage({ apiUrl }) {
  const [income, setIncome] = useState(0);
  const [expectedRemaining, setExpectedRemaining] = useState(0);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  
  // บัญชีและพื้นที่รวมโอน
  const [accounts, setAccounts] = useState([]);
  const [transferGroups, setTransferGroups] = useState({}); // { accountId: [expId, expId] }

  const [filterCategory, setFilterCategory] = useState("ทั้งหมด");
  const [showModal, setShowModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  
  // สำหรับขยายรูป QR Code
  const [zoomedQrImage, setZoomedQrImage] = useState(null);

  const [form, setForm] = useState({
    id: null,
    name: "",
    amount: "",
    category: "อาหาร",
  });

  const [accountForm, setAccountForm] = useState({
    id: null,
    name: "",
    accountNumber: "",
    qrImage: "",
  });

  const [checkedItems, setCheckedItems] = useState({});
  const [checkedMonth, setCheckedMonth] = useState(`${new Date().getFullYear()}-${new Date().getMonth()}`);
  const [syncStatus, setSyncStatus] = useState("");

  const [isLoaded, setIsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setIsLoaded(false);
    setSyncStatus('กำลังโหลดข้อมูลจาก Google Sheets…');
    request(apiUrl, 'getPlanner').then(data => {
      if (!active) return;
      const cloud = data || {};
      if (typeof cloud !== 'object' || Array.isArray(cloud)) throw Error('รูปแบบข้อมูลแผนรายจ่ายไม่ถูกต้อง');
      const extras = readExtras(apiUrl);
      const month = `${new Date().getFullYear()}-${new Date().getMonth()}`;
      const loaded = { income: cloud.income ?? 0, expenses: cloud.expenses || [], categories: cloud.categories || DEFAULT_CATEGORIES,
        checkedItems: cloud.checkedMonth === month ? (cloud.checkedItems || {}) : {}, checkedMonth: month,
        accounts: extras.accounts, transferGroups: extras.transferGroups };
      if (!Array.isArray(loaded.expenses) || !Array.isArray(loaded.categories)) throw Error('รูปแบบข้อมูลแผนรายจ่ายไม่ถูกต้อง');
      setOriginalData(cloud);
      setIncome(loaded.income); setExpenses(loaded.expenses); setCategories(loaded.categories);
      setCheckedItems(loaded.checkedItems); setCheckedMonth(month);
      setAccounts(loaded.accounts); setTransferGroups(loaded.transferGroups);
      setSavedSnapshot(JSON.stringify(loaded));
      setIsLoaded(true); setSyncStatus('โหลดข้อมูลจาก Google Sheets แล้ว');
    }).catch(error => { if (active) setSyncStatus(error.message); });
    return () => { active = false; };
  }, [apiUrl, retry]);
  const globalTotalExpense = expenses.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
  useEffect(() => { setExpectedRemaining(income - globalTotalExpense); }, [income, globalTotalExpense]);
  const snapshot = JSON.stringify({ income, expenses, categories, checkedItems, checkedMonth, accounts, transferGroups });
  const dirty = isLoaded && snapshot !== savedSnapshot;
  useEffect(() => {
    if (!dirty) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const saveToCache = async () => {
    if (!isLoaded || saving) return;
    setSaving(true); setSyncStatus('กำลังบันทึก…');
    try {
      writeExtras(apiUrl, { accounts, transferGroups });
      await request(apiUrl, 'savePlanner', { data: { ...originalData, income, expectedRemaining: income - globalTotalExpense,
        expenses, categories, checkedItems, checkedMonth } });
      setSavedSnapshot(snapshot); setSyncStatus('บันทึก Google Sheets แล้ว • บัญชีและ QR เก็บในเครื่องนี้');
    } catch (error) { setSyncStatus(error.message); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!dirty) return;
    const timer = setTimeout(() => saveToCache(), 2000);
    return () => clearTimeout(timer);
    // Save only when the editable snapshot changes; failures remain visible for manual retry.
  }, [snapshot, isLoaded, savedSnapshot]);

  // --- การจัดการรายการรายจ่าย ---
  const handleOpenAdd = () => {
    const initialCat = categories.length > 0 ? categories[0].label : "";
    setForm({ id: null, name: "", amount: "", category: initialCat });
    setShowModal(true);
  };

  const handleEdit = (exp) => {
    setForm({
      id: exp.id,
      name: exp.name,
      amount: exp.amount,
      category: exp.category,
    });
    setShowModal(true);
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    if (window.confirm("ลบรายการนี้ใช่ไหม?")) {
      setExpenses(expenses.filter((exp) => exp.id !== id));
      // เอารายการออกจากทุกช่องบัญชี
      setTransferGroups(prev => {
        const newGroups = { ...prev };
        Object.keys(newGroups).forEach(key => {
          newGroups[key] = newGroups[key].filter(expId => expId !== id);
        });
        return newGroups;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.id) {
      setExpenses(expenses.map((exp) => (exp.id === form.id ? { ...form, id: exp.id } : exp)));
    } else {
      setExpenses([...expenses, { ...form, id: Date.now().toString() }]);
    }
    setShowModal(false);
  };

  const toggleChecked = (id, e) => {
    e.stopPropagation();
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getCatInfo = (catName) => {
    return categories.find((c) => c.label === catName) || { emoji: "📌", color: "#94A3B8" };
  };

  // --- การจัดการบัญชีรับเงิน ---
  const handleSubmitAccount = (e) => {
    e.preventDefault();
    if (accountForm.id) {
      setAccounts(accounts.map((acc) => (acc.id === accountForm.id ? { ...accountForm, id: acc.id } : acc)));
    } else {
      setAccounts([...accounts, { ...accountForm, id: Date.now().toString() }]);
    }
    setShowAccountModal(false);
  };

  const handleDeleteAccount = (id) => {
    if (window.confirm("ลบบัญชีนี้ใช่ไหม?")) {
      setAccounts(accounts.filter(a => a.id !== id));
      setTransferGroups(prev => {
        const newGroups = { ...prev };
        delete newGroups[id];
        return newGroups;
      });
    }
  };

  // --- ระบบ Drag & Drop เข้าช่องบัญชี ---
  const handleDragStart = (e, id) => {
    e.dataTransfer.setData("expId", id);
    e.dataTransfer.effectAllowed = "copyMove";
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDropToAccount = (e, accountId) => {
    e.preventDefault();
    const expId = e.dataTransfer.getData("expId");
    if (expId) {
      setTransferGroups(prev => {
        const newGroups = { ...prev };
        // เอาออกจากช่องอื่นก่อน เพื่อไม่ให้มันซ้ำซ้อนกัน
        Object.keys(newGroups).forEach(key => {
          newGroups[key] = newGroups[key].filter(id => id !== expId);
        });
        if (!newGroups[accountId]) newGroups[accountId] = [];
        if (!newGroups[accountId].includes(expId)) {
          newGroups[accountId].push(expId);
        }
        return newGroups;
      });
    }
  };

  const removeFromTransferGroup = (accountId, expId) => {
    setTransferGroups(prev => {
      const newGroups = { ...prev };
      if (newGroups[accountId]) {
        newGroups[accountId] = newGroups[accountId].filter(id => id !== expId);
      }
      return newGroups;
    });
  };

  const clearTransferGroup = (accountId) => {
    setTransferGroups(prev => {
      const newGroups = { ...prev };
      newGroups[accountId] = [];
      return newGroups;
    });
  };

  const filterChipsData = Object.entries(expenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + parseFloat(exp.amount);
    return acc;
  }, {})).map(([key, value]) => ({
    name: key,
    value,
    percent: globalTotalExpense > 0 ? ((value / globalTotalExpense) * 100).toFixed(0) : 0,
    ...getCatInfo(key),
  })).sort((a, b) => b.value - a.value);

  const filteredExpenses = filterCategory === "ทั้งหมด" ? expenses : expenses.filter((exp) => exp.category === filterCategory);

  const displayTotalExpense = filteredExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
  const displayChartData = Object.entries(filteredExpenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + parseFloat(exp.amount);
    return acc;
  }, {})).map(([key, value]) => ({ name: key, value, ...getCatInfo(key) })).sort((a, b) => b.value - a.value);

  if (!isLoaded) return <div role="status" className="p-8 text-slate-300"><p>{syncStatus}</p><button className="mt-4 rounded-xl bg-slate-800 px-4 py-2" onClick={() => setRetry(n => n + 1)}>โหลดอีกครั้ง</button></div>;
  return (
    <fieldset disabled={saving} className="min-w-0 border-0 p-0 m-0">
    <div className="max-w-7xl mx-auto pb-24 animate-fadeIn px-4 pt-8 relative">
      <div className="absolute right-4 top-2 flex items-center gap-3 h-6">
        <span className="text-[10px] font-medium text-slate-400">{syncStatus}</span>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold">แผนรายจ่าย</h1><p className="text-xs text-slate-400 mt-2">รายจ่ายใช้ Google Sheets เดิม • บัญชีรับเงินและ QR เก็บในเบราว์เซอร์นี้</p></div>
        <button disabled={saving || !dirty} onClick={saveToCache} className="bg-indigo-600 disabled:opacity-50 rounded-xl px-4 py-3">{saving ? 'กำลังบันทึก…' : dirty ? 'บันทึกการเปลี่ยนแปลง' : 'บันทึกแล้ว'}</button>
      </div>
      {dirty && <p role="status" className="text-amber-300 mb-4">มีการแก้ไข กรุณาบันทึกก่อนเปลี่ยนหน้า</p>}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-2">
        {/* ================= ซ้าย: ภาพรวม (Income & Summary) ================= */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl relative overflow-hidden">
            <div className="absolute -right-6 -top-6 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="mb-6">
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2">💰 รายรับเดือนนี้</p>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-slate-500">฿</span>
                <input
                  type="number"
                  className="bg-transparent text-4xl font-bold text-white outline-none w-full border-b border-slate-700 focus:border-emerald-500 pb-1 transition-colors"
                  value={income || ""}
                  placeholder="0"
                  onChange={(e) => setIncome(parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="p-4 bg-slate-950 rounded-2xl flex justify-between items-center border border-slate-800">
              <div>
                <p className="text-[10px] text-slate-500 mb-1">คาดว่าจะเหลือเงิน</p>
                <p className={`text-xl font-bold ${expectedRemaining >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {expectedRemaining >= 0 ? "+" : ""}฿{expectedRemaining.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-slate-500 mb-1">รวมที่ต้องจ่าย</p>
                <p className="text-xl font-bold text-red-400">-฿{globalTotalExpense.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-5 rounded-3xl border border-slate-800 shadow-xl">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-1 h-4 bg-indigo-500 rounded-full"></span> สรุปรายจ่าย
            </h3>
            <div className="flex flex-col items-center gap-6">
              <div className="relative w-48 h-48 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={displayChartData} innerRadius={65} outerRadius={85} paddingAngle={5} dataKey="value" stroke="none">
                      {displayChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "#0f172a", borderColor: "#334155", borderRadius: "8px", color: "#fff", fontSize: "12px" }} itemStyle={{ color: "#fff" }} formatter={(val) => `฿${val.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-[10px] text-slate-500">รายจ่ายรวม</span>
                  <span className="text-sm font-bold text-white">{displayTotalExpense.toLocaleString()}</span>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-wrap gap-2 justify-center">
                  <button onClick={() => setFilterCategory("ทั้งหมด")} className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${filterCategory === "ทั้งหมด" ? "bg-indigo-600/20 text-indigo-400 border-indigo-500/50" : "bg-slate-950 text-slate-400 border-slate-800 hover:bg-slate-800"}`}>ทั้งหมด</button>
                  {filterChipsData.map((cat) => (
                    <button key={cat.name} onClick={() => setFilterCategory(cat.name)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all" style={filterCategory === cat.name ? { borderColor: cat.color, color: cat.color, backgroundColor: `${cat.color}1A` } : { borderColor: "#1e293b", color: "#94a3b8", backgroundColor: "#020617" }}>
                      <span>{cat.emoji}</span><span>{cat.name}</span>{cat.percent > 0 && <span className="opacity-60 font-normal">({cat.percent}%)</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={handleOpenAdd} className="w-full mt-6 py-3 border border-dashed border-slate-700 text-indigo-400 hover:text-indigo-300 hover:border-indigo-500/50 hover:bg-indigo-500/10 rounded-xl font-bold flex items-center justify-center gap-2 transition text-xs">
              <IconPlus size={16} /> เพิ่มรายการที่ต้องจ่าย
            </button>
          </div>
        </div>

        {/* ================= ขวา: บัญชีรับเงิน และ รายการ ================= */}
        <div className="lg:col-span-7 space-y-8">
          
          {/* --- พื้นที่จัดการบัญชี และ ช่องรับลากรวมยอด --- */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-bold text-sm flex items-center gap-2">
                <span className="text-xl">💳</span> บัญชีและยอดโอน
              </h3>
              <button onClick={() => { setAccountForm({id:null, name:"", accountNumber:"", qrImage:""}); setShowAccountModal(true); }} className="text-xs bg-indigo-600/20 text-indigo-400 px-3 py-1.5 rounded-lg font-bold hover:bg-indigo-600/40 transition">
                + เพิ่มบัญชี
              </button>
            </div>
            
            {/* เปลี่ยนเป็น Grid บรรทัดละ 2 ช่อง (sm:grid-cols-2) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {accounts.length === 0 ? (
                <div className="col-span-1 sm:col-span-2 text-center py-10 text-slate-500 bg-slate-900/50 rounded-3xl border-2 border-slate-800 border-dashed text-xs">
                  <span className="text-3xl block mb-2 opacity-50">📥</span>
                  ยังไม่มีบัญชีรับเงิน<br/>กด "+ เพิ่มบัญชี" มุมขวาบน เพื่อสร้างช่องสำหรับลากรวมยอด
                </div>
              ) : (
                accounts.map(acc => {
                  const groupExpIds = transferGroups[acc.id] || [];
                  const groupTotal = groupExpIds.reduce((sum, id) => {
                    const exp = expenses.find(e => e.id === id);
                    return sum + (exp ? parseFloat(exp.amount) : 0);
                  }, 0);

                  return (
                    <div 
                      key={acc.id}
                      onDrop={(e) => handleDropToAccount(e, acc.id)}
                      onDragOver={handleDragOver}
                      className={`rounded-3xl border-2 transition-all p-5 flex flex-col ${groupExpIds.length > 0 ? "border-emerald-500/50 bg-emerald-900/10 shadow-[0_0_15px_rgba(16,185,129,0.1)]" : "border-slate-700 border-dashed bg-slate-900/50"}`}
                    >
                      <div className="flex justify-between items-start mb-2 border-b border-slate-800/50 pb-3">
                        <div className="flex-1 pr-2">
                          <p className="text-white font-bold text-base truncate">{acc.name}</p>
                          {acc.accountNumber && (
                            <p className="text-indigo-400 text-xs font-mono font-bold mt-1 tracking-wider bg-indigo-500/10 inline-block px-2 py-0.5 rounded-md border border-indigo-500/20">{acc.accountNumber}</p>
                          )}
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => { setAccountForm(acc); setShowAccountModal(true); }} className="text-slate-500 hover:text-indigo-400 text-sm">✏️</button>
                          <button onClick={() => handleDeleteAccount(acc.id)} className="text-slate-500 hover:text-red-400 text-sm">✕</button>
                        </div>
                      </div>

                      <div className="my-2">
                        <p className="text-[10px] text-slate-500 mb-0.5">ยอดรวมที่ต้องโอนเข้าบัญชีนี้</p>
                        <p className="text-2xl font-bold text-emerald-400">฿{groupTotal.toLocaleString()}</p>
                      </div>

                      <div className="min-h-[48px] bg-slate-950/50 rounded-xl p-2 mb-3 border border-slate-800/50 flex-1">
                        {groupExpIds.length === 0 ? (
                          <div className="h-full flex items-center justify-center text-xs text-slate-600 opacity-70">
                            ลากการ์ดรายจ่ายมาใส่ที่นี่
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            {groupExpIds.map(id => {
                              const exp = expenses.find(e => e.id === id);
                              if (!exp) return null;
                              return (
                                <div key={id} className="flex justify-between items-center bg-slate-800 px-2 py-1.5 rounded-lg text-xs border border-slate-700">
                                  <span className="text-slate-300 truncate max-w-[60%]">{exp.name}</span>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-emerald-400 font-bold">฿{parseFloat(exp.amount).toLocaleString()}</span>
                                    <button onClick={() => removeFromTransferGroup(acc.id, id)} className="text-slate-500 hover:text-red-400 bg-slate-900 w-5 h-5 rounded flex items-center justify-center">✕</button>
                                  </div>
                                </div>
                              );
                            })}
                            <button onClick={() => clearTransferGroup(acc.id)} className="text-[10px] text-red-400/80 text-center mt-2 hover:text-red-400 transition block w-full py-1 bg-red-500/5 rounded-md">ล้างรายการทั้งหมด</button>
                          </div>
                        )}
                      </div>

                      {acc.qrImage ? (
                        <div 
                          className="flex justify-center bg-white p-2 rounded-xl border border-slate-200 shadow-inner mt-auto cursor-zoom-in hover:scale-105 transition-transform group relative"
                          onClick={() => setZoomedQrImage(acc.qrImage)}
                        >
                          <img src={acc.qrImage} className="h-32 object-contain rounded-lg" alt="QR" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                            <span className="text-white text-xs font-bold px-2 py-1 bg-black/50 rounded-lg">🔍 กดเพื่อขยาย</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4 bg-slate-800 rounded-xl border border-slate-700 border-dashed mt-auto">
                          <span className="text-xl block mb-1">📵</span>
                          <span className="text-[10px] text-slate-500">ไม่ได้แนบ QR Code</span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-end mb-4 px-1">
              <h3 className="text-white font-bold text-sm">รายการ {filterCategory === "ทั้งหมด" ? "ทั้งหมด" : `หมวด: ${filterCategory}`}</h3>
              <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-1 rounded-full border border-slate-700 flex items-center gap-1">
                <span className="text-sm">👆</span> ลากการ์ดขึ้นไปรวมยอด
              </span>
            </div>
            
            {filteredExpenses.length > 0 && (() => {
              const doneCount = filteredExpenses.filter((e) => checkedItems[e.id]).length;
              const totalCount = filteredExpenses.length;
              const pct = Math.round((doneCount / totalCount) * 100);
              return (
                <div className="mb-4 bg-slate-900 p-3 rounded-xl border border-slate-800 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-xs text-slate-400">จ่ายแล้ว {doneCount}/{totalCount} รายการ</span>
                      <span className="text-xs font-bold text-emerald-400">{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                  {doneCount === totalCount && totalCount > 0 && <span className="text-lg">🎉</span>}
                </div>
              );
            })()}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredExpenses.map((exp) => {
                const catInfo = getCatInfo(exp.category);
                const isDone = !!checkedItems[exp.id];
                
                const inAccounts = accounts.filter(acc => (transferGroups[acc.id] || []).includes(exp.id));

                return (
                  <div
                    key={exp.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, exp.id)}
                    onClick={() => handleEdit(exp)}
                    className={`bg-slate-900 p-4 rounded-xl border flex justify-between items-center hover:bg-slate-800/80 transition relative overflow-hidden group active:cursor-grabbing ${isDone ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-800"}`}
                    style={{ cursor: 'grab' }}
                    title="คลิกค้างเพื่อลาก หรือ คลิกเพื่อแก้ไข"
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: isDone ? "#10b981" : catInfo.color }}></div>
                    
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-30 transition pointer-events-none text-white flex flex-col gap-0.5">
                       <div className="w-1 h-1 rounded-full bg-white"></div>
                       <div className="w-1 h-1 rounded-full bg-white"></div>
                       <div className="w-1 h-1 rounded-full bg-white"></div>
                    </div>

                    <div className="flex items-center gap-3 pl-4">
                      <button onClick={(e) => toggleChecked(exp.id, e)} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${isDone ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-600 hover:border-emerald-500"}`}>
                        {isDone && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                      </button>
                      <div className="w-10 h-10 rounded-full bg-slate-950 flex items-center justify-center border border-slate-800 text-lg shadow-inner">{catInfo.emoji}</div>
                      <div>
                        <p className={`font-bold text-sm flex items-center gap-1.5 flex-wrap ${isDone ? "text-slate-500 line-through" : "text-white"}`}>
                          {exp.name} 
                          {inAccounts.map(acc => (
                             <span key={acc.id} title={`อยู่ในช่อง ${acc.name}`} className="text-[9px] bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-500/30">
                               → {acc.name}
                             </span>
                          ))}
                        </p>
                        <p className="text-[10px] mt-0.5" style={{ color: isDone ? "#64748b" : catInfo.color }}>
                          {exp.category} {isDone && " — จ่ายแล้ว ✓"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className={`font-bold text-sm ${isDone ? "text-emerald-500/60 line-through" : "text-red-400"}`}>
                        -฿{parseFloat(exp.amount).toLocaleString()}
                      </p>
                      <button onClick={(e) => handleDelete(exp.id, e)} className="text-slate-600 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition focus:opacity-100 z-10 relative">
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
              {filteredExpenses.length === 0 && (
                <div className="col-span-1 sm:col-span-2 text-center py-8 text-slate-500 bg-slate-900/50 rounded-2xl border border-slate-800 border-dashed">
                  <p className="text-sm">ไม่มีรายการในหมวดหมู่นี้</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <PlanModal
          form={form}
          setForm={setForm}
          handleSubmit={handleSubmit}
          setShowModal={setShowModal}
          categories={categories}
          setCategories={setCategories}
          expenses={expenses}
          setExpenses={setExpenses}
        />
      )}
      
      {showAccountModal && (
        <AccountModal
          form={accountForm}
          setForm={setAccountForm}
          handleSubmit={handleSubmitAccount}
          setShowModal={setShowAccountModal}
        />
      )}

      {/* --- Modal ขยายรูป QR Code --- */}
      {zoomedQrImage && (
        <div 
          className="fixed inset-0 bg-slate-950/95 backdrop-blur-md flex items-center justify-center z-[100] p-4 cursor-zoom-out animate-fadeIn"
          onClick={() => setZoomedQrImage(null)}
        >
          <div className="relative">
            <img 
              src={zoomedQrImage} 
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-[0_0_50px_rgba(255,255,255,0.1)] bg-white p-2" 
              alt="Zoomed QR" 
            />
            <button 
              className="absolute -top-4 -right-4 text-white bg-slate-800 border border-slate-700 rounded-full w-10 h-10 flex items-center justify-center hover:bg-red-500 transition shadow-lg"
              onClick={(e) => { e.stopPropagation(); setZoomedQrImage(null); }}
            >
              <IconX />
            </button>
            <p className="text-center text-slate-400 mt-4 text-sm">กดที่ใดก็ได้เพื่อปิด</p>
          </div>
        </div>
      )}
    </div>
    </fieldset>
  );
}
